import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { AttiFormComponent } from 'src/app/common/form/atti-form.component';
import { Atto } from 'src/app/common/models/atto';
import { DocumentoAllegato } from 'src/app/common/models/documento-allegato';
import { DataInput } from 'src/app/common/models/dto/data-input';
import { UpdateMetadatiDocumentoRequest } from 'src/app/common/models/dto/update-metadati-documento-request';
import { FascicoloFull } from 'src/app/common/models/fascicolo/fascicolo-full';
import { ServicePathsSettings } from 'src/app/core/configuration/service-paths-settings';
import { FileManagerService } from 'src/app/core/components/upload-tool/services/file-manager.service';
import { HelpersService } from 'src/app/core/services/helpers.service';
import { OperationsService } from 'src/app/core/services/operations.service';
import { SpinnerService } from 'src/app/common/components/spinner-load/spinner.service';
import { CreazionePraticaTipologicheService } from '../../../services/creazione-pratica-tipologiche.service';

@Component({
  selector: 'step-atti-form',
  templateUrl: '../../../../../../common/form/atti-form.component.html',
})
export class StepAttiFormComponent extends AttiFormComponent implements OnInit, OnChanges {
  @Output() sendAttoEvent: EventEmitter<Atto> = new EventEmitter<Atto>();
  @Output() showListEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() annullaModificheEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() pulisciCampiEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() onDocumentiCaricati: EventEmitter<void> = new EventEmitter<void>();
  @Input() attoToEdit: Atto;
  @Input() attoAssociato: Atto;
  @Input() startAsEdit!: boolean;
  @Input() fascicolo!: FascicoloFull;

  isReset: boolean;

  constructor(
    @Inject(OperationsService) operationService: OperationsService,
    @Inject(FormBuilder) campiForm: FormBuilder,
    private tipologicheService: CreazionePraticaTipologicheService,
    @Inject(HelpersService) helpers: HelpersService,
    private http: HttpClient,
    private fileManager: FileManagerService,
    private spinner: SpinnerService,
  ) {
    super(operationService, campiForm, helpers);
    this.isGestioneDocumenti = true;
  }

  ngOnInit() {
    this.isInserimento(true);
    this.isReset = false;
    this.inizializza();
    if (this.attoToEdit !== undefined) {
      this.isInserimento(false);
    }
  }

  ngOnChanges() {
    if (this.attoToEdit !== undefined && !this.attoAssociato) {
      this.isInserimento(false);
      this.inizializza(true);
    }
    else {
      this.ngOnInit();
    }
  }

  getAtto(): Atto {
    if (this.attoToEdit === undefined && this.attoAssociato === undefined) {
      return new Atto();
    }
    else if (this.isReset) {
      this.attoToEdit = undefined;
      this.attoAssociato = undefined;
      this.isReset = false;
      return new Atto();
    }
    else if (this.attoToEdit !== undefined && !this.isReset) {
      return this.attoToEdit;
    }
    else {
      return this.attoAssociato;
    }
  }

  onAnnullaModifiche() {
    this.attoToEdit = undefined;
    this.isReset = false;
    this.ngOnInit();
    this.showListEvent.emit();
    this.annullaModificheEvent.emit();
  }

  onPulisciCampi() {
    this.isReset = true;
    this.attoAssociato = undefined;
    this.createForm();
    this.resetDocForm();
    this.pulisciCampiEvent.emit();
  }

  getRequest(request: Atto) {
    this.attoToEdit = undefined;
    this.isReset = false;
    this.sendAttoEvent.emit(request);
    this.request = new Atto();
    this.attiForm.reset();
    this.attoAssociato = undefined;
  }

  getFascicolo(): any {
    return this.fascicolo;
  }

  inizializza(isEdit = false) {
    this.createForm(isEdit);
    if (!isEdit) {
      this.initDocForm();
    }
  }

  override onSearchChange(files: any) {
    if (!files?.length) return;
    this.docLoading = true;
    this.spinner.show();
    const uploads$ = Array.from(files).map((file: any) =>
      this.fileManager.getPayloadFile(file).pipe(
        switchMap(payload => this.uploadFile(payload).pipe(
          catchError(err => { console.error('Errore upload:', err); return of(null); })
        ))
      )
    );
    forkJoin(uploads$).subscribe({
      next: (responses: any[]) => {
        responses.forEach(res => {
          if (res?.fileData) this.setFileDataRequest(res.fileData);
        });
      },
      complete: () => {
        this.docLoading = false;
        this.spinner.hide();
      }
    });
  }

  uploadPendingDocs(attoId: string) {
    if (!this.fileUploadList?.length) {
      this.onDocumentiCaricati.emit();
      return;
    }
    const files = this.fileUploadList.splice(0, this.fileUploadList.length).map(f => this.enrichDocFile(f));
    forkJoin(
      files.map(f =>
        this.operationService.addDocumentoToEntity(ServicePathsSettings.URL_ADD_DOCUMENTO_TO_ATTO, attoId, f)
          .pipe(map((res: any) => ({ res, file: f })))
      )
    ).pipe(
      switchMap(results => {
        const metadatiObs = results
          .filter(({ res }) => res.returnCode === 'OK')
          .map(({ res, file }) => {
            const docs: DocumentoAllegato[] = res?.atto?.documenti ?? [];
            const saved = docs.find((d: DocumentoAllegato) => d.refDocumentale === file.refDocumentale);
            return saved ? this.updateMetadatiDocumento(saved) : of(null);
          });
        return metadatiObs.length ? forkJoin(metadatiObs) : of([]);
      })
    ).subscribe({
      next: () => { this.onDocumentiCaricati.emit(); },
      error: () => { this.onDocumentiCaricati.emit(); }
    });
  }

  private uploadFile(payload: any): Observable<any> {
    return this.http.post<any>(
      ServicePathsSettings.DOMINIO + ServicePathsSettings.URL_UPLOAD_DOCUMENTO,
      payload,
      { withCredentials: true }
    );
  }

  private enrichDocFile(file: DocumentoAllegato): DocumentoAllegato {
    const { signedData, ...fileWithoutSignedData } = file;
    const titolo = file.nomeFile
      ? file.nomeFile.replace(/\.[^/.]+$/, '')
      : (this.docFormRequest.titolo?.trim() || '');
    return {
      ...fileWithoutSignedData,
      titolo,
      tipoDocumento: this.docFormRequest.tipoDocumento,
      dataRiferimento: this.docFormRequest.dataRiferimento,
      provenienza: this.docFormRequest.provenienza || null,
      tipoVisibilitaDocumento: this.docFormRequest.tipoVisibilitaDocumento,
      ...(this.docFormRequest.note ? { note: '' + this.docFormRequest.note } : {})
    };
  }

  private updateMetadatiDocumento(documento: DocumentoAllegato): Observable<any> {
    const request = new UpdateMetadatiDocumentoRequest();
    request.documento = { ...documento };
    if (this.fascicolo?.praticaBase) {
      request.dataInput = new DataInput(
        this.fascicolo.idParent,
        this.fascicolo.id,
        this.fascicolo.praticaBase.posizione,
        this.fascicolo.posizione,
        this.fascicolo.praticaBase.tipoPratica.id
      );
    }
    return this.operationService.updateMetadatiDocumento(request);
  }
}
